import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
} from '@tanstack/react-router';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import type { MeResponse } from '@wit-support/admin-contracts';
import { AppShell } from './components/app-shell.js';
import { LoginPage } from './routes/login.js';
import { OAuthConsentPage, OAuthLoginPage } from './routes/oauth.js';
import { oauthPageFor } from './lib/oauth-flow.js';
import { Dashboard } from './routes/dashboard.js';
import {
  TimezoneProvider,
  detectBrowserTimezone,
  setAttachmentsBaseUrl,
} from '@wit-support/ui-domain';
import { Button } from '@wit-support/ui';
import { fetchMe } from './lib/api.js';
import { landingRedirect, moduleAccessGuard } from './lib/module-guard.js';
import { landingPath, navModulesFor, readStoredWorkspace } from './lib/workspace-nav.js';
import { moduleRouteStaticData } from './lib/route-layout.js';
import { env } from './env.js';
import {
  accountingModule,
  AccountingModulePermsProvider,
  setAccountingApiBaseUrl,
} from '@wit-support/admin-module-accounting/client';
import {
  adsLauncherModule,
  AdsLauncherModulePermsProvider,
  adsLauncherPermsFrom,
  setAdsLauncherApiBaseUrl,
} from '@wit-support/admin-module-ads-launcher/client';
import {
  adsPaybackModule,
  setAdsPaybackApiBaseUrl,
} from '@wit-support/admin-module-ads-payback/client';
import {
  adsCampaignsModule,
  AdsCampaignsModulePermsProvider,
  adsCampaignsPermsFrom,
  setAdsCampaignsApiBaseUrl,
} from '@wit-support/admin-module-ads-campaigns/client';
import {
  adminsModule,
  AdminsModulePermsProvider,
  setAdminApiBaseUrl,
} from '@wit-support/admin-module-admins/client';
import {
  annotationsModule,
  AnnotationsModulePermsProvider,
  setAnnotationsApiBaseUrl,
} from '@wit-support/admin-module-annotations/client';
import {
  channelsModule,
  ChannelsModulePermsProvider,
  setChannelsApiBaseUrl,
} from '@wit-support/admin-module-channels/client';
import {
  chargebacksModule,
  ChargebacksModulePermsProvider,
  setChargebacksApiBaseUrl,
} from '@wit-support/admin-module-chargebacks/client';
import {
  contentModule,
  ContentModulePermsProvider,
  setContentApiBaseUrl,
} from '@wit-support/admin-module-content/client';
import {
  socialModule,
  SocialModulePermsProvider,
  setSocialApiBaseUrl,
} from '@wit-support/admin-module-social/client';
import {
  bulkSelection,
  conversationsModule,
  ConversationsModulePermsProvider,
  setConversationsApiBaseUrl,
} from '@wit-support/admin-module-conversations/client';
import {
  templatesModule,
  TemplatesModulePermsProvider,
  setTemplatesApiBaseUrl,
} from '@wit-support/admin-module-templates/client';
import {
  classificationModule,
  ClassificationModulePermsProvider,
  setClassificationApiBaseUrl,
} from '@wit-support/admin-module-classification/client';
import { insightsModule, setInsightsApiBaseUrl } from '@wit-support/admin-module-insights/client';
import {
  settingsModule,
  SettingsModulePermsProvider,
  setSettingsApiBaseUrl,
} from '@wit-support/admin-module-settings/client';
import {
  telegramModule,
  TelegramModulePermsProvider,
  setTelegramApiBaseUrl,
} from '@wit-support/admin-module-telegram/client';
import {
  pipelinesModule,
  PipelinesModulePermsProvider,
  setPipelinesApiBaseUrl,
} from '@wit-support/admin-module-pipelines/client';
import { identityModule, setIdentityApiBaseUrl } from '@wit-support/admin-module-identity/client';
import {
  paymentModule,
  PaymentModulePermsProvider,
  setPaymentApiBaseUrl,
} from '@wit-support/admin-module-payment/client';
import {
  blacklistModule,
  BlacklistModulePermsProvider,
  setBlacklistApiBaseUrl,
} from '@wit-support/admin-module-blacklist/client';
import { registerPwa } from './pwa/register.js';
import { bootReturnTo } from './lib/return-to.js';
import { API_ORIGIN } from './lib/api-origin.js';
import { useLeaveForReturnTo } from './lib/use-return-to.js';

// Register the service worker before the router mounts.
registerPwa();

// `?returnTo=` (the closed MCP docs on admin-api) is captured once at boot into
// sessionStorage so it survives the sign-in; MCP OAuth pages drop it.
bootReturnTo(window.location.pathname, window.location.search, API_ORIGIN);

// Register each module's API base URL at boot.
setAccountingApiBaseUrl(env.VITE_ADMIN_API_URL);
setAdminApiBaseUrl(env.VITE_ADMIN_API_URL);
setAdsLauncherApiBaseUrl(env.VITE_ADMIN_API_URL);
setAdsPaybackApiBaseUrl(env.VITE_ADMIN_API_URL);
setAdsCampaignsApiBaseUrl(env.VITE_ADMIN_API_URL);
setAnnotationsApiBaseUrl(env.VITE_ADMIN_API_URL);
setChannelsApiBaseUrl(env.VITE_ADMIN_API_URL);
setChargebacksApiBaseUrl(env.VITE_ADMIN_API_URL);
setContentApiBaseUrl(env.VITE_ADMIN_API_URL);
setSocialApiBaseUrl(env.VITE_ADMIN_API_URL);
setConversationsApiBaseUrl(env.VITE_ADMIN_API_URL);
setTemplatesApiBaseUrl(env.VITE_ADMIN_API_URL);
setClassificationApiBaseUrl(env.VITE_ADMIN_API_URL);
setInsightsApiBaseUrl(env.VITE_ADMIN_API_URL);
setSettingsApiBaseUrl(env.VITE_ADMIN_API_URL);
setPipelinesApiBaseUrl(env.VITE_ADMIN_API_URL);
setIdentityApiBaseUrl(env.VITE_ADMIN_API_URL);
setPaymentApiBaseUrl(env.VITE_ADMIN_API_URL);
setBlacklistApiBaseUrl(env.VITE_ADMIN_API_URL);
setTelegramApiBaseUrl(env.VITE_ADMIN_API_URL);
setAttachmentsBaseUrl(env.VITE_ADMIN_API_URL);

/**
 * Static module registry. Each module declares its own routes + nav entry.
 * Adding a module is a two-line change here plus a new workspace package.
 */
export const MODULES = [
  conversationsModule,
  annotationsModule,
  adminsModule,
  channelsModule,
  chargebacksModule,
  contentModule,
  socialModule,
  accountingModule,
  adsLauncherModule,
  adsPaybackModule,
  adsCampaignsModule,
  telegramModule,
  templatesModule,
  classificationModule,
  pipelinesModule,
  insightsModule,
  settingsModule,
  identityModule,
  paymentModule,
  blacklistModule,
].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

/**
 * The single `['me']` query definition. Shared verbatim by `MeGate`'s
 * `useQuery` and by the route guards' `ensureQueryData` so both read the SAME
 * cache entry with the SAME retry policy — a guard must never kick off a
 * second, differently-configured fetch of the identity the shell is already
 * loading.
 *
 * `fetchMe` resolves to `null` on a genuine 401 and THROWS only on transient
 * failures (5xx / network), which is what makes the guard's error handling
 * below safe to reason about.
 */
const meQueryOptions = {
  queryKey: ['me'] as const,
  queryFn: () => fetchMe(env.VITE_ADMIN_API_URL),
  retry: 3,
  retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
};

/**
 * Where a permission-denied navigation lands. Derived from the module's own id
 * rather than written as a literal: the anti-loop check in the guard compares
 * this against `/${mod.id}`, so a rename of `conversationsModule.id` would
 * silently disarm it if the two were spelled independently. Every SUPPORT role
 * holds `conversations.list`, which is what makes this module their fallback;
 * a scoped role without it (`marketing`) lands on its own first module instead
 * (`landingForMe`).
 */
const FALLBACK_PATH = `/${conversationsModule.id}`;
const FALLBACK_PERMISSION = conversationsModule.permission;

/** First available module of the stored / first workspace — same rule as `/`. */
const landingForMe = (me: MeResponse): string | null =>
  landingPath(navModulesFor(MODULES, me.permissions), readStoredWorkspace());

export const MeContext = createContext<MeResponse | null>(null);
export function useMe(): MeResponse {
  const m = useContext(MeContext);
  if (!m) throw new Error('useMe outside provider');
  return m;
}

function MeGate({ children }: { children: ReactNode }) {
  const { data, isPending, isError, refetch, isFetching } = useQuery(meQueryOptions);
  // Already signed in and sent here by admin-api with `?returnTo=…/docs/mcp`:
  // go back there instead of opening the panel (sign-in itself is handled by
  // `LoginPage`).
  useLeaveForReturnTo(Boolean(data), API_ORIGIN);
  // Reset the module-scoped inbox bulk-selection whenever the authenticated
  // identity changes (logout → null, or a different operator logs in on the same
  // SPA instance without a page reload). Without this, a server-side 401 swaps
  // to the login screen without re-evaluating the module, so the next operator
  // would inherit the previous one's bulk mode + checked rows.
  useEffect(() => {
    bulkSelection.reset();
  }, [data?.admin.id]);
  // Initial load (or a retrying transient error) — keep the operator waiting,
  // never bounce to login.
  if (isPending) return null;
  // Transient failure that exhausted retries: show a retry affordance instead
  // of forcing a logout. The session may still be perfectly valid.
  if (isError) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <p>Не удалось связаться с сервером. Проверьте соединение.</p>
        <Button type="button" onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? 'Повтор…' : 'Повторить'}
        </Button>
      </div>
    );
  }
  // Only an explicit 401 (data === null) means "not logged in".
  if (!data) return <LoginPage />;
  return (
    <MeContext.Provider value={data}>
      <TimezoneProvider tz={data.timezone || detectBrowserTimezone()}>
        <ModulePermsProviders me={data}>
          <AppShell me={data}>{children}</AppShell>
        </ModulePermsProviders>
      </TimezoneProvider>
    </MeContext.Provider>
  );
}

/**
 * Fold a list of provider wrappers into one nesting, outermost first.
 *
 * `reduceRight` reproduces the hand-written pyramid exactly, so the rendered
 * tree is unchanged — but adding a module is now one line instead of one more
 * indentation level for everything below it. Each `perms` literal still lives
 * inside its own provider's JSX, so it is still type-checked against that
 * module's own context shape; nothing is widened to a common type.
 */
function composeProviders(
  wrappers: ReadonlyArray<(children: ReactNode) => ReactNode>,
  children: ReactNode,
): ReactNode {
  return wrappers.reduceRight<ReactNode>((acc, wrap) => wrap(acc), children);
}

/**
 * Hands every `admin-module-*` the permission booleans it needs, derived from
 * the single `/api/me` payload. Modules never read `me` themselves — that keeps
 * them decoupled from the shell AND keeps `['me']` to exactly one observer.
 */
function ModulePermsProviders({ me, children }: { me: MeResponse; children: ReactNode }) {
  const can = (key: string): boolean => me.permissions.includes(key);
  return composeProviders(
    [
      (c) => (
        <AdminsModulePermsProvider
          perms={{
            canCreate: can('admins.create'),
            canUpdate: can('admins.update'),
            canDisable: can('admins.disable'),
          }}
        >
          {c}
        </AdminsModulePermsProvider>
      ),
      (c) => (
        <AnnotationsModulePermsProvider
          perms={{
            canManageTags: can('tag.manage'),
            // НЕ право на ленту, а право на СПИСОК АДМИНОВ: без него фильтр по
            // автору не рисуется вовсе (`GET /api/admins` закрыт `admins.list`).
            canListAdmins: can('admins.list'),
            // Чужие комментарии в ленте: изменить/удалить — только с
            // `comment.manage`; свои — по автору (`me.admin.id`).
            canManageComments: can('comment.manage'),
            currentAdminId: me.admin.id,
          }}
        >
          {c}
        </AnnotationsModulePermsProvider>
      ),
      (c) => (
        <ChannelsModulePermsProvider perms={{ canManage: can('channels.create') }}>
          {c}
        </ChannelsModulePermsProvider>
      ),
      (c) => (
        <ClassificationModulePermsProvider perms={{ canManage: can('topics.manage') }}>
          {c}
        </ClassificationModulePermsProvider>
      ),
      (c) => (
        <PipelinesModulePermsProvider perms={{ canManage: can('pipelines.manage') }}>
          {c}
        </PipelinesModulePermsProvider>
      ),
      (c) => (
        <BlacklistModulePermsProvider perms={{ canManage: can('blacklist.manage') }}>
          {c}
        </BlacklistModulePermsProvider>
      ),
      (c) => (
        <TelegramModulePermsProvider
          perms={{
            canManage: can('telegram_destinations.manage'),
            canRerenderCards: can('telegram.manage'),
          }}
        >
          {c}
        </TelegramModulePermsProvider>
      ),
      (c) => (
        <TemplatesModulePermsProvider perms={{ canManage: can('template.manage') }}>
          {c}
        </TemplatesModulePermsProvider>
      ),
      (c) => (
        <SettingsModulePermsProvider
          perms={{
            canReadToggles: can('llm.read'),
            canManageToggles: can('llm.manage'),
            canReadBilling: can('billing_settings.read'),
            canManageBilling: can('billing_settings.manage'),
            canManageRisk: can('risk_settings.manage'),
            canManageNotifications: can('notification_settings.manage'),
          }}
        >
          {c}
        </SettingsModulePermsProvider>
      ),
      (c) => (
        <ContentModulePermsProvider perms={{ canManage: can('content.manage') }}>
          {c}
        </ContentModulePermsProvider>
      ),
      (c) => (
        <SocialModulePermsProvider
          perms={{
            canManage: can('social.manage'),
            canManageConnections: can('social.connections_manage'),
          }}
        >
          {c}
        </SocialModulePermsProvider>
      ),
      (c) => (
        <AccountingModulePermsProvider perms={{ canManage: can('accounting.manage') }}>
          {c}
        </AccountingModulePermsProvider>
      ),
      (c) => (
        // Семь флагов раздела «Реклама → Запуски FB» из ключей `ads.*`.
        <AdsLauncherModulePermsProvider perms={adsLauncherPermsFrom(can)}>
          {c}
        </AdsLauncherModulePermsProvider>
      ),
      (c) => (
        // Два флага раздела «Реклама → Кампании»: пауза — `ads.launch`, включение — `ads.activate`.
        <AdsCampaignsModulePermsProvider perms={adsCampaignsPermsFrom(can)}>
          {c}
        </AdsCampaignsModulePermsProvider>
      ),
      (c) => (
        <PaymentModulePermsProvider perms={{ canRunAdminOps: can('payment.admin_ops') }}>
          {c}
        </PaymentModulePermsProvider>
      ),
      (c) => (
        <ChargebacksModulePermsProvider
          perms={{
            canIdentify: can('chargebacks.identify'),
            canAction: can('chargebacks.action'),
            canManageAccounts: can('chargebacks.accounts_manage'),
            canManageStatus: can('chargebacks.case_status'),
            canManageRules: can('chargebacks.rules_manage'),
          }}
        >
          {c}
        </ChargebacksModulePermsProvider>
      ),
      (c) => (
        <ConversationsModulePermsProvider
          perms={{
            canReply: can('conversation.reply'),
            canMerge: can('conversation.merge'),
            canReclassify: can('conversation.reclassify'),
            canBlacklist: can('blacklist.manage'),
            canViewPipelineSteps: can('pipelines.read'),
            canViewIdentityHistory: can('identity.read'),
            canSearchIdentity: can('identity.search'),
            canAttachIdentity: can('identity.attach'),
            canReadSubscription: can('subscription.read'),
            canCancelSubscription: can('subscription.cancel'),
            canReadPortraits: can('portraits.read'),
            canReadProductUsage: can('productUsage.read'),
            canReadChargebacks: can('chargebacks.read'),
            canOverrideSenderCapacity: can('conversation.sender_capacity_override'),
            canReadSettings: can('settings.read'),
            canTag: can('conversation.tag'),
            canComment: can('conversation.comment'),
            canManageComments: can('comment.manage'),
            currentAdminDisplayName: me.admin.displayName,
            currentAdminId: me.admin.id,
          }}
        >
          {c}
        </ConversationsModulePermsProvider>
      ),
    ],
    children,
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <MeGate>
      <Outlet />
    </MeGate>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // Своей главной у админки нет: `/` ведёт в первый доступный модуль
  // сохранённого или первого доступного пространства. `Dashboard` — русская
  // заглушка на случай, когда доступных модулей нет.
  beforeLoad: () =>
    landingRedirect({
      loadMe: () => qc.ensureQueryData(meQueryOptions),
      modules: MODULES,
      readStored: readStoredWorkspace,
    }),
  component: Dashboard,
});

function moduleRoutes() {
  const routes = [];
  for (const mod of MODULES) {
    for (const r of mod.routes) {
      const basePath = `/${mod.id}`;
      const joined = r.path === '/' ? basePath : `${basePath}${r.path}`;
      routes.push(
        createRoute({
          getParentRoute: () => rootRoute,
          path: joined,
          component: r.component,
          // Раскладка экрана в шелле (`'page'` / `'fullscreen'`) и хром
          // (`'page'` / `'shell'`, маршрут или `routeChrome` модуля): AppShell
          // читает их из совпавших маршрутов, см. `shellLayoutOf`/`shellChromeOf`.
          staticData: moduleRouteStaticData(r, mod),
          beforeLoad: () =>
            moduleAccessGuard({
              loadMe: () => qc.ensureQueryData(meQueryOptions),
              permission: mod.permission,
              basePath,
              fallbackPath: FALLBACK_PATH,
              fallbackPermission: FALLBACK_PERMISSION,
              landing: landingForMe,
            }),
        }),
      );
    }
  }
  return routes;
}

const router = createRouter({
  routeTree: rootRoute.addChildren([indexRoute, ...moduleRoutes()]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  // MCP OAuth pages (`routes/oauth.tsx`) sit OUTSIDE the router and `MeGate`:
  // `/oauth/login` must render the login form even for a logged-in operator
  // (`prompt=login`), and both pages leave with a full-page navigation to the
  // authorization server's continuation, so no in-app routing is involved.
  const oauthPage = oauthPageFor(window.location.pathname);
  return (
    <QueryClientProvider client={qc}>
      {oauthPage === 'login' ? (
        <OAuthLoginPage />
      ) : oauthPage === 'consent' ? (
        <OAuthConsentPage />
      ) : (
        <RouterProvider router={router} />
      )}
    </QueryClientProvider>
  );
}
